import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import { GetBankStatementItemsResponse } from "@/models/api/finances/GetBankStatementItemsResponse";
import { GetBankStatementDocumentsResponse } from "@/models/api/finances/GetBankStatementDocumentsResponse";
import { BANK_STATEMENT_TYPES } from "@/static/bankStatementTypes";
import {
  GetNotSettledDocument,
  GetNotSettledDocumentsResponse,
} from "@/models/api/finances/GetNotSettledDocumentsResponse";
import { ModifyBankStatementItem } from "@/models/api/finances/ModifyBankStatementItem";
import { DocumentsAmount } from "@/models/api/finances/DocumentsAmount";
import { BankStatementMatchDocument, BankStatementMatchReservation } from "@/models/api/finances/BankStatementMatch";
import { PaymentForBankStatement } from "@/models/api/finances/PaymentForBankStatement";
import { GetBankStatementReservationsResponse } from "@/models/api/finances/GetBankStatementReservationsResponse";

//Bank statement
export const URL_GET_BANK_STATEMENT_ITEMS = "/bankStatement/Items/${BankStatementType}/Paged";
export const URL_GET_BANK_STATEMENT_ITEM_MATCH_DATA = "/bankStatement/Paged";
export const URL_POST_REMOVE_BANK_STATEMENT_ITEMS = "/bankStatement/Remove";
export const URL_POST_REMOVE_BANK_STATEMENT_ITEM = "/bankStatement/${BankStatementItemId}/RemoveItem";
export const URL_GET_DATA_TO_MODIFY_BANK_STATEMENT_ITEM =
  "/bankStatement/${BankStatementItemId}/DataToModifyBankStatementItem";
export const URL_GET_RECALCULATE_MODIFY_BANK_STATEMENT_ITEM =
  "/bankStatement/${BankStatementItemId}/RecalculateModifyBankStatementItem";
export const URL_POST_MODIFY_BANK_STATEMENT_ITEM = "/bankStatement/${BankStatementItemId}/ModifyBankStatementItem";

export const URL_GET_BANK_STATEMENT_IS_EDITING_LOCKED = "/bankStatement/${BankStatementId}/IsEditingLocked";

//Document
export const URL_GET_DOCUMENTS_TO_STATEMENT = "/bankStatement/${BankStatementItemId}/Documents/Paged";
export const URL_GET_DOCUMENTS_MATCHED_TO_STATEMENT = "/bankStatement/${BankStatementItemId}/DocumentsMatched/Paged";
export const URL_GET_NOT_SETTLED_DOCUMENTS_PAGED = "/bankStatement/NotSettledDocument/Paged";
export const URL_POST_SETTLE_DOCUMENTS_WITH_BANK_STATEMENT_ITEM =
  "/bankStatement/${BankStatementItemId}/SettleDocuments";
export const URL_GET_FILTERED_DOCUMENTS = "/bankStatement/FilteredDocuments";
export const URL_GET_SELECTED_DOCUMENTS_AMOUNT = "/bankStatement/${BankStatementItemId}/SelectedDocumentsAmount";

export const URL_POST_ADD_AUTOMATICALLY_MATCH_DOCUMENT =
  "/bankStatement/${BankStatementItemId}/AutomaticallyMatch/${DocumentId}";
export const URL_POST_ADD_MANUAL_MATCH_DOCUMENT = "/bankStatement/${BankStatementItemId}/ManualMatch/${DocumentId}";
export const URL_POST_REMOVE_BANK_STATEMENT_ITEM_MATCH_DOCUMENT =
  "/bankStatement/${BankStatementItemId}/RemoveMatch/${DocumentId}";

export const URL_GET_BANK_STATEMENT_ITEM_MATCH_DATA_DOCUMENT =
  "/bankStatement/${BankStatementItemId}/MatchData/${DocumentId}";
export const URL_GET_MANUAL_BANK_STATEMENT_ITEM_MATCH_DATA_DOCUMENT =
  "/bankStatement/${BankStatementItemId}/ManualMatchData/${DocumentId}";

export const URL_GET_DOCUMENT_PAYMENTS_FOR_BANK_STATEMENT_ITEM =
  "/payment/${DocumentId}/DocumentPaymentsForBankStatementItem";
export const URL_POST_REMOVE_MATCH_FOR_MANUAL_TRANSFER =
  "/bankStatement/${BankStatementItemId}/RemoveMatchForManualTransfer/${PaymentId}";
export const URL_POST_MATCH_BANK_STATEMENT_ITEM_FOR_MANUAL_TRANSFER =
  "/bankStatement/${BankStatementItemId}/MatchBankStatementItemForManualTransfer/${PaymentId}";

//Reservation
export const URL_GET_RESERVATIONS_TO_STATEMENT = "/bankStatement/${BankStatementItemId}/Reservations/Paged";
export const URL_GET_RESERVATIONS_MATCHED_TO_STATEMENT =
  "/bankStatement/${BankStatementItemId}/ReservationsMatched/Paged";

export const URL_POST_ADD_AUTOMATICALLY_MATCH_RESERVATION =
  "/bankStatement/${BankStatementItemId}/AutomaticallyMatchReservation/${BookingItemId}";
export const URL_POST_ADD_MANUAL_MATCH_RESERVATION =
  "/bankStatement/${BankStatementItemId}/ManualMatchReservation/${BookingItemId}";
export const URL_POST_REMOVE_BANK_STATEMENT_ITEM_MATCH_RESERVATION =
  "/bankStatement/${BankStatementItemId}/RemoveMatchReservation/${BookingItemId}";

export const URL_GET_BANK_STATEMENT_ITEM_MATCH_DATA_RESERVATION =
  "/bankStatement/${BankStatementItemId}/MatchDataReservation/${BookingItemId}";
export const URL_GET_MANUAL_BANK_STATEMENT_ITEM_MATCH_DATA_RESERVATION =
  "/bankStatement/${BankStatementItemId}/ManualMatchDataReservation/${BookingItemId}";

export const URL_GET_RESERVATION_PAYMENTS_FOR_BANK_STATEMENT_ITEM =
  "/payment/${ReservationId}/ReservationPaymentsForBankStatementItem";
export const URL_POST_REMOVE_MATCH_RESERVATION_FOR_MANUAL_TRANSFER =
  "/bankStatement/${BankStatementItemId}/RemoveMatchReservationForManualTransfer/${PaymentId}";
export const URL_POST_MATCH_BANK_STATEMENT_ITEM_RESERVATION_FOR_MANUAL_TRANSFER =
  "/bankStatement/${BankStatementItemId}/MatchBankStatementItemReservationForManualTransfer/${PaymentId}";
export const URL_POST_REMOVE_RESERVATION_PAYMENT = "/bankStatement/${PaymentId}/RemoveReservationPayment";
export const URL_POST_CHANGE_BANK_STATEMENT_ITEM_CONTRACTOR =
  "/bankStatement/${BankStatementItemId}/ChangeBankStatementItemContractor/${ContractorId}";

const _getBankStatementItemsTable = new CancellableHelper<GetBankStatementItemsResponse>(
  URL_GET_BANK_STATEMENT_ITEMS,
  GetBankStatementItemsResponse
);
_getBankStatementItemsTable.jsonSerializer = true;
export const getBankStatementItemsTable = async (
  genericTableRequest: GenericTableRequest,
  bankStatementType: BANK_STATEMENT_TYPES
): Promise<GetBankStatementItemsResponse> => {
  return await _getBankStatementItemsTable.get({
    ...genericTableRequest,
    BankStatementType: bankStatementType,
  });
};

const _getDocumentsMatchedToStatementTable = new CancellableHelper<GetBankStatementDocumentsResponse>(
  URL_GET_DOCUMENTS_MATCHED_TO_STATEMENT,
  GetBankStatementDocumentsResponse
);
_getDocumentsMatchedToStatementTable.jsonSerializer = true;
export const getDocumentsMatchedToStatementTable = async (
  genericTableRequest: GenericTableRequest,
  bankStatementItemId: number
): Promise<GetBankStatementDocumentsResponse> => {
  return await _getDocumentsMatchedToStatementTable.get({
    ...genericTableRequest,
    BankStatementItemId: bankStatementItemId,
  });
};

const _getDocumentsToStatementTable = new CancellableHelper<GetBankStatementDocumentsResponse>(
  URL_GET_DOCUMENTS_TO_STATEMENT,
  GetBankStatementDocumentsResponse
);
_getDocumentsToStatementTable.jsonSerializer = true;
export const getDocumentsToStatementTable = async (
  genericTableRequest: GenericTableRequest,
  bankStatementItemId: number
): Promise<GetBankStatementDocumentsResponse> => {
  return await _getDocumentsToStatementTable.get({
    ...genericTableRequest,
    BankStatementItemId: bankStatementItemId,
  });
};

const _getReservationsMatchedToStatementTable = new CancellableHelper<GetBankStatementReservationsResponse>(
  URL_GET_RESERVATIONS_MATCHED_TO_STATEMENT,
  GetBankStatementReservationsResponse
);
_getReservationsMatchedToStatementTable.jsonSerializer = true;
export const getReservationsMatchedToStatementTable = async (
  genericTableRequest: GenericTableRequest,
  bankStatementItemId: number
): Promise<GetBankStatementReservationsResponse> => {
  return await _getReservationsMatchedToStatementTable.get({
    ...genericTableRequest,
    BankStatementItemId: bankStatementItemId,
  });
};

const _getReservationsToStatementTable = new CancellableHelper<GetBankStatementReservationsResponse>(
  URL_GET_RESERVATIONS_TO_STATEMENT,
  GetBankStatementReservationsResponse
);
_getReservationsToStatementTable.jsonSerializer = true;
export const getReservationsToStatementTable = async (
  genericTableRequest: GenericTableRequest,
  bankStatementItemId: number
): Promise<GetBankStatementReservationsResponse> => {
  return await _getReservationsToStatementTable.get({
    ...genericTableRequest,
    BankStatementItemId: bankStatementItemId,
  });
};

const _removeBankStatementItems = new CancellableHelper<null>(URL_POST_REMOVE_BANK_STATEMENT_ITEMS, null, true);
export const removeBankStatementItems = async (bankStatementItemIds: Array<number>, comment: string): Promise<null> => {
  return await _removeBankStatementItems.post(
    {
      comment: comment,
      bankStatementItemIds: bankStatementItemIds,
    },
    null
  );
};

const _removeBankStatementItem = new CancellableHelper<null>(URL_POST_REMOVE_BANK_STATEMENT_ITEM, null, true);
export const removeBankStatementItem = async (bankStatementItemId: number, comment: string): Promise<null> => {
  return await _removeBankStatementItem.post(
    {
      comment: comment,
      BankStatementItemId: bankStatementItemId,
    },
    null
  );
};

const _getNotSettledDocumentsPaged = new CancellableHelper<GetNotSettledDocumentsResponse>(
  URL_GET_NOT_SETTLED_DOCUMENTS_PAGED,
  GetNotSettledDocumentsResponse
);
_getNotSettledDocumentsPaged.jsonSerializer = true;
export const getNotSettledDocumentsPaged = async (
  genericTableRequest: GenericTableRequest
): Promise<GetNotSettledDocumentsResponse> => {
  return await _getNotSettledDocumentsPaged.get(genericTableRequest);
};

const _getDataToModifyBankStatementItem = new CancellableHelper<ModifyBankStatementItem>(
  URL_GET_DATA_TO_MODIFY_BANK_STATEMENT_ITEM,
  ModifyBankStatementItem
);
export const getDataToModifyBankStatementItem = async (
  bankStatementItemId: number
): Promise<ModifyBankStatementItem> => {
  return await _getDataToModifyBankStatementItem.get({
    BankStatementItemId: bankStatementItemId,
  });
};

const _isBankStatementLocked = new CancellableHelper<boolean>(URL_GET_BANK_STATEMENT_IS_EDITING_LOCKED, "boolean");
export const isBankStatementLocked = async (bankStatementItemId: number) => {
  return _isBankStatementLocked.get({
    BankStatementItemId: bankStatementItemId,
  });
};

const _recalculateModifyBankStatementItem = new CancellableHelper<ModifyBankStatementItem>(
  URL_GET_RECALCULATE_MODIFY_BANK_STATEMENT_ITEM,
  ModifyBankStatementItem
);
export const recalculateModifyBankStatementItem = async (
  bankStatementItemId: number,
  modifyValue: number
): Promise<ModifyBankStatementItem> => {
  return await _recalculateModifyBankStatementItem.get({
    BankStatementItemId: bankStatementItemId,
    ModifyValue: modifyValue,
  });
};

const _modifyBankStatementItem = new CancellableHelper<null>(URL_POST_MODIFY_BANK_STATEMENT_ITEM, null, true);
export const modifyBankStatementItem = async (
  bankStatementItemId: number,
  modifyValue: number,
  comment: string
): Promise<null> => {
  return await _modifyBankStatementItem.post({
    BankStatementItemId: bankStatementItemId,
    ModifyValue: modifyValue,
    Comment: comment,
  });
};

const _getFilteredDocuments = new CancellableHelper<Array<GetNotSettledDocument>>(
  URL_GET_FILTERED_DOCUMENTS,
  GetNotSettledDocument
);
_getFilteredDocuments.jsonSerializer = true;
export const getFilteredDocuments = async (
  genericTableRequest: GenericTableRequest
): Promise<Array<GetNotSettledDocument>> => {
  return await _getFilteredDocuments.get(genericTableRequest);
};

const _postSelectedDocumentsAmount = new CancellableHelper<DocumentsAmount>(
  URL_GET_SELECTED_DOCUMENTS_AMOUNT,
  DocumentsAmount
);
export const postSelectedDocumentsAmount = async (
  bankStatementItemId: number,
  documentIds: Array<string>
): Promise<DocumentsAmount> => {
  return await _postSelectedDocumentsAmount.post({ BankStatementItemId: bankStatementItemId }, documentIds);
};

const _settleDocumentsWithBankStatementItem = new CancellableHelper<null>(
  URL_POST_SETTLE_DOCUMENTS_WITH_BANK_STATEMENT_ITEM,
  null,
  true
);
export const settleDocumentsWithBankStatementItem = async (
  bankStatementItemId: number,
  documentIds: Array<string>
): Promise<null> => {
  return await _settleDocumentsWithBankStatementItem.post({ BankStatementItemId: bankStatementItemId }, documentIds);
};

export const _getBankStatementItemMatchDataDocument = new CancellableHelper<BankStatementMatchDocument>(
  URL_GET_BANK_STATEMENT_ITEM_MATCH_DATA_DOCUMENT,
  BankStatementMatchDocument
);
export const getBankStatementItemMatchDataDocument = async (
  bankStatementItemId: number,
  documentId: string
): Promise<BankStatementMatchDocument> => {
  return await _getBankStatementItemMatchDataDocument.get({
    BankStatementItemId: bankStatementItemId,
    DocumentId: documentId,
  });
};

export const _getManualBankStatementItemMatchDataDocument = new CancellableHelper<BankStatementMatchDocument>(
  URL_GET_MANUAL_BANK_STATEMENT_ITEM_MATCH_DATA_DOCUMENT,
  BankStatementMatchDocument
);
export const getManualBankStatementItemMatchDataDocument = async (
  bankStatementItemId: number,
  documentId: string,
  paymentAmountInCurrency: number
): Promise<BankStatementMatchDocument> => {
  return await _getManualBankStatementItemMatchDataDocument.get({
    BankStatementItemId: bankStatementItemId,
    DocumentId: documentId,
    PaymentAmountInCurrency: paymentAmountInCurrency,
  });
};

const _addAutomaticallyMatchDocument = new CancellableHelper<null>(
  URL_POST_ADD_AUTOMATICALLY_MATCH_DOCUMENT,
  null,
  true
);
export const addAutomaticallyMatchDocument = async (bankStatementItemId: number, documentId: string): Promise<null> => {
  return await _addAutomaticallyMatchDocument.post(
    {
      BankStatementItemId: bankStatementItemId,
      DocumentId: documentId,
    },
    null
  );
};

const _addManualMatchDocument = new CancellableHelper<null>(URL_POST_ADD_MANUAL_MATCH_DOCUMENT, null, true);
export const addManualMatchDocument = async (
  bankStatementItemId: number,
  documentIds: string,
  paymentAmountInCurrency: number
): Promise<null> => {
  return await _addManualMatchDocument.post(
    {
      BankStatementItemId: bankStatementItemId,
      DocumentId: documentIds,
      PaymentAmountInCurrency: paymentAmountInCurrency,
    },
    null
  );
};

const _removeBankStatementItemMatchDocument = new CancellableHelper<boolean>(
  URL_POST_REMOVE_BANK_STATEMENT_ITEM_MATCH_DOCUMENT,
  "boolean"
);
export const removeBankStatementItemMatchDocument = async (
  bankStatementItemId: number,
  documentIds: string
): Promise<boolean> => {
  return await _removeBankStatementItemMatchDocument.post(
    {
      BankStatementItemId: bankStatementItemId,
      DocumentId: documentIds,
    },
    null
  );
};

export const _getBankStatementItemMatchDataReservation = new CancellableHelper<BankStatementMatchReservation>(
  URL_GET_BANK_STATEMENT_ITEM_MATCH_DATA_RESERVATION,
  BankStatementMatchReservation
);
export const getBankStatementItemMatchDataReservation = async (
  bankStatementItemId: number,
  bookingItemId: number
): Promise<BankStatementMatchReservation> => {
  return await _getBankStatementItemMatchDataReservation.get({
    BankStatementItemId: bankStatementItemId,
    BookingItemId: bookingItemId,
  });
};

export const _getManualBankStatementItemMatchDataReservation = new CancellableHelper<BankStatementMatchReservation>(
  URL_GET_MANUAL_BANK_STATEMENT_ITEM_MATCH_DATA_RESERVATION,
  BankStatementMatchReservation
);
export const getManualBankStatementItemMatchDataReservation = async (
  bankStatementItemId: number,
  bookingItemId: number,
  paymentAmountInCurrency: number
): Promise<BankStatementMatchReservation> => {
  return await _getManualBankStatementItemMatchDataReservation.get({
    BankStatementItemId: bankStatementItemId,
    BookingItemId: bookingItemId,
    PaymentAmountInCurrency: paymentAmountInCurrency,
  });
};

const _addAutomaticallyMatchReservation = new CancellableHelper<null>(
  URL_POST_ADD_AUTOMATICALLY_MATCH_RESERVATION,
  null,
  true
);
export const addAutomaticallyMatchReservation = async (
  bankStatementItemId: number,
  bookingItemId: number
): Promise<null> => {
  return await _addAutomaticallyMatchReservation.post(
    {
      BankStatementItemId: bankStatementItemId,
      BookingItemId: bookingItemId,
    },
    null
  );
};

const _addManualMatchReservation = new CancellableHelper<null>(URL_POST_ADD_MANUAL_MATCH_RESERVATION, null, true);
export const addManualMatchReservation = async (
  bankStatementItemId: number,
  bookingItemId: number,
  paymentAmountInCurrency: number
): Promise<null> => {
  return await _addManualMatchReservation.post(
    {
      BankStatementItemId: bankStatementItemId,
      BookingItemId: bookingItemId,
      PaymentAmountInCurrency: paymentAmountInCurrency,
    },
    null
  );
};

const _removeBankStatementItemMatchReservation = new CancellableHelper<boolean>(
  URL_POST_REMOVE_BANK_STATEMENT_ITEM_MATCH_RESERVATION,
  "boolean"
);
export const removeBankStatementItemMatchReservation = async (
  bankStatementItemId: number,
  bookingItemId: number
): Promise<boolean> => {
  return await _removeBankStatementItemMatchReservation.post(
    {
      BankStatementItemId: bankStatementItemId,
      BookingItemId: bookingItemId,
    },
    null
  );
};

export const _getDocumentPaymentsForBankStatementItem = new CancellableHelper<Array<PaymentForBankStatement>>(
  URL_GET_DOCUMENT_PAYMENTS_FOR_BANK_STATEMENT_ITEM,
  PaymentForBankStatement
);
export const getDocumentPaymentsForBankStatementItem = async (
  documentId: string
): Promise<Array<PaymentForBankStatement>> => {
  return await _getDocumentPaymentsForBankStatementItem.get({
    DocumentId: documentId,
  });
};

const _removeMatchForManualTransfer = new CancellableHelper<null>(
  URL_POST_REMOVE_MATCH_FOR_MANUAL_TRANSFER,
  null,
  true
);
export const removeMatchForManualTransfer = async (bankStatementItemId: number, paymentId: number): Promise<null> => {
  return await _removeMatchForManualTransfer.post(
    {
      BankStatementItemId: bankStatementItemId,
      PaymentId: paymentId,
    },
    null
  );
};

const _matchBankStatementItemForManualTransfer = new CancellableHelper<null>(
  URL_POST_MATCH_BANK_STATEMENT_ITEM_FOR_MANUAL_TRANSFER,
  null,
  true
);
export const matchBankStatementItemForManualTransfer = async (
  bankStatementItemId: number,
  paymentId: number
): Promise<null> => {
  return await _matchBankStatementItemForManualTransfer.post(
    {
      BankStatementItemId: bankStatementItemId,
      PaymentId: paymentId,
    },
    null
  );
};

export const _getReservationPaymentsForBankStatementItem = new CancellableHelper<Array<PaymentForBankStatement>>(
  URL_GET_RESERVATION_PAYMENTS_FOR_BANK_STATEMENT_ITEM,
  PaymentForBankStatement
);
export const getReservationPaymentsForBankStatementItem = async (
  reservationId: number
): Promise<Array<PaymentForBankStatement>> => {
  return await _getReservationPaymentsForBankStatementItem.get({
    ReservationId: reservationId,
  });
};

const _removeMatchReservationForManualTransfer = new CancellableHelper<null>(
  URL_POST_REMOVE_MATCH_RESERVATION_FOR_MANUAL_TRANSFER,
  null,
  true
);
export const removeMatchReservationForManualTransfer = async (
  bankStatementItemId: number,
  paymentId: number
): Promise<null> => {
  return await _removeMatchReservationForManualTransfer.post(
    {
      BankStatementItemId: bankStatementItemId,
      PaymentId: paymentId,
    },
    null
  );
};

const _matchBankStatementItemReservationForManualTransfer = new CancellableHelper<null>(
  URL_POST_MATCH_BANK_STATEMENT_ITEM_RESERVATION_FOR_MANUAL_TRANSFER,
  null,
  true
);
export const matchBankStatementItemReservationForManualTransfer = async (
  bankStatementItemId: number,
  paymentId: number
): Promise<null> => {
  return await _matchBankStatementItemReservationForManualTransfer.post(
    {
      BankStatementItemId: bankStatementItemId,
      PaymentId: paymentId,
    },
    null
  );
};

const _removeReservationPayment = new CancellableHelper<null>(URL_POST_REMOVE_RESERVATION_PAYMENT, null, true);
export const removeReservationPayment = async (paymentId: number): Promise<null> => {
  return await _removeReservationPayment.post({ PaymentId: paymentId }, null);
};

const _changeBankStatementItemContractor = new CancellableHelper<null>(
  URL_POST_CHANGE_BANK_STATEMENT_ITEM_CONTRACTOR,
  null,
  true
);
export const changeBankStatementItemContractor = async (
  bankStatementItemId: number,
  contractorId: string | null
): Promise<null> => {
  return await _changeBankStatementItemContractor.post(
    { BankStatementItemId: bankStatementItemId, ContractorId: contractorId },
    null
  );
};
